import { Module } from "vuex";

import { RootState } from "@/store";

export type LocalJobStatus = "PENDING" | "SUBMITTED" | "INPROGRESS" | "COMPLETED" | "FAILED" | "STOPPED";

export interface LocalJobScenario {
  scenarioId: string;
  scenarioName: string;
  status: LocalJobStatus;
}

export interface LocalJob {
  jobId: string;
  projectId: string;
  projectName: string;
  date: string;
  status: LocalJobStatus;
  scenarios: LocalJobScenario[];
}

export interface LocalJobsState {
  jobsById: Record<string, LocalJob>;
}

const LS_JOBS_KEY = "planCreate_local_jobs";
const MAX_JOBS = 10;

const loadFromLS = (): Record<string, LocalJob> => {
  try {
    const raw = localStorage.getItem(LS_JOBS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const persist = (jobsById: Record<string, LocalJob>) => {
  localStorage.setItem(LS_JOBS_KEY, JSON.stringify(jobsById && typeof jobsById === "object" ? jobsById : {}));
};

const ensureMap = (v: unknown): Record<string, LocalJob> =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, LocalJob>) : {};

const trimToMax = (map: Record<string, LocalJob>, max = MAX_JOBS): Record<string, LocalJob> => {
  const arr = Object.values(map || []);
  arr.sort((a, b) => {
    const ta = Date.parse(a?.date ?? "") || 0;
    const tb = Date.parse(b?.date ?? "") || 0;
    return tb - ta;
  });
  const trimmed = arr.slice(0, max);
  const result: Record<string, LocalJob> = {};
  for (const j of trimmed) result[j.jobId] = j;
  return result;
};

const state: LocalJobsState = {
  jobsById: trimToMax(loadFromLS()),
};

const localJobs: Module<LocalJobsState, RootState> = {
  namespaced: true,
  state,

  mutations: {
    setAll(state, map: Record<string, LocalJob>) {
      state.jobsById = trimToMax(ensureMap(map));
      persist(state.jobsById);
    },
    upsertJob(state, job: LocalJob) {
      const current = ensureMap(state.jobsById);
      current[job.jobId] = job;
      state.jobsById = trimToMax(current);
      persist(state.jobsById);
    },
    updateStatus(state, payload: { jobId: string; status: LocalJobStatus; scenarioId?: string }) {
      state.jobsById = ensureMap(state.jobsById);
      const job = state.jobsById[payload.jobId];
      if (!job) return;
      if (payload.scenarioId) {
        const sc = job.scenarios.find((s) => s.scenarioId === payload.scenarioId);
        if (sc) sc.status = payload.status;
      } else {
        job.status = payload.status;
      }
      persist(state.jobsById);
    },
    clearAll(state) {
      state.jobsById = {};
      persist(state.jobsById);
    },
  },

  actions: {
    rehydrateFromLS({ commit }) {
      commit("setAll", loadFromLS());
    },
    upsertJob({ commit }, job: LocalJob) {
      commit("upsertJob", job);
    },
    updateStatus({ commit }, p: { jobId: string; status: LocalJobStatus; scenarioId?: string }) {
      commit("updateStatus", p);
    },
    clearAll({ commit }) {
      commit("clearAll");
    },
  },

  getters: {
    hasActiveJobs: (state): boolean => Object.keys(ensureMap(state.jobsById)).length > 0,
    all: (state): LocalJob[] => Object.values(ensureMap(state.jobsById)),
    byJobId:
      (state) =>
      (jobId: string): LocalJob | undefined =>
        ensureMap(state.jobsById)[jobId],
    byProject:
      (state) =>
      (projectId: string): LocalJob[] =>
        Object.values(ensureMap(state.jobsById)).filter((j) => j.projectId === projectId),
  },
};

export default localJobs;
